import { z } from 'zod';

import { groupForTypeCode, NOTIFICATION_CATEGORY } from './constants';

// Two layers, as in the devices feature: `*Wire` mirrors the payload, and the exported type is
// the normalised shape components consume.
//
// The contract behind this file was **probed live on 2026-08-28** by Tracking Mobile with a real
// grower token (greenway_mobile/docs/MOCKS.md §8). The published spec documents the routes in
// prose with no response schema, so the shapes below were captured from live responses seeded
// through the backend's own `test-push` route:
//
//   { "id": "uuid", "typeCode": "WARRANTY_APPROVED", "title": "…", "description": "…",
//     "isRead": false, "createdAt": "2026-08-28T08:47:01.977Z", "deviceId": null,
//     "navigationType": "WARRANTY_DETAIL", "navigationTargetId": null }
//
// with `meta: { nextCursor, hasNextPage, unreadCount, hasUnread }`.
//
// An earlier version of this file was written against mobile's *superseded* PROPOSED contract
// (`type`, `read`/`readAt`, `referenceId`, `items`), which the server never implemented — every
// row parsed to an unread `alert` with no reference id. Those spellings are kept as **fallbacks**
// rather than deleted, because they are still what the preferences MSW seam emits and they cost
// one `??` each.

/** A wire id: the backend sends uuids, the fixtures send numbers. */
const wireId = z.union([z.string(), z.number()]).transform(String);

export const notificationWireSchema = z
  .object({
    id: wireId,
    /** The live field: a ~31-value enum. `type` is the superseded contract's lowercase spelling. */
    typeCode: z.string().nullish(),
    type: z.string().nullish(),
    title: z.string().catch(''),
    description: z.string().catch(''),
    createdAt: z.string(),
    /** The live read flag. `read`/`readAt` are the superseded contract's. */
    isRead: z.boolean().nullish(),
    read: z.boolean().nullish(),
    readAt: z.string().nullish(),
    deviceId: wireId.nullish(),
    /**
     * What the row should open, and the id to open it with. These are the live contract's
     * navigation fields and they are the **only** source of a ticket/claim id — the superseded
     * `referenceId` does not exist on the wire, which is why every support and warranty row used
     * to fall back to its listing instead of opening the record.
     */
    navigationType: z.string().nullish(),
    navigationTargetId: wireId.nullish(),
    referenceId: wireId.nullish(),
  })
  .transform((row) => ({
    id: row.id,
    group: groupForTypeCode(row.typeCode ?? row.type),
    /** The raw wire code, kept so a screen can be more specific than the eight groups allow. */
    typeCode: row.typeCode ?? row.type ?? null,
    title: row.title,
    description: row.description,
    createdAt: row.createdAt,
    read: row.isRead ?? row.read ?? Boolean(row.readAt),
    deviceId: row.deviceId ?? null,
    navigationType: row.navigationType ?? null,
    referenceId: row.navigationTargetId ?? row.referenceId ?? null,
  }));

export type Notification = z.infer<typeof notificationWireSchema>;

/** `meta` — the same object whether it arrives beside `data` or nested inside it. */
const metaWireSchema = z
  .object({
    nextCursor: z.string().nullish(),
    hasNextPage: z.boolean().nullish(),
    unreadCount: z.number().nullish(),
    hasUnread: z.boolean().nullish(),
  })
  .partial()
  .passthrough();

type NotificationMeta = z.infer<typeof metaWireSchema>;

const rowsFrom = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    const box = value as Record<string, unknown>;
    for (const key of ['notifications', 'items', 'data'] as const) {
      if (Array.isArray(box[key])) return box[key] as unknown[];
    }
  }
  return [];
};

const metaFrom = (value: unknown): NotificationMeta => {
  if (!value || typeof value !== 'object') return {};
  const parsed = metaWireSchema.safeParse(value);
  return parsed.success ? parsed.data : {};
};

/**
 * One page of notifications, parsed from the **whole** success body.
 *
 * This takes the envelope unpeeled (`apiClient.getEnvelope`) rather than the `{ data }` payload,
 * because the live contract puts `meta` **beside** `data`, not inside it the way the devices
 * listing does. `apiClient.get` peels `data` and drops everything else, which silently cost this
 * feature both its cursor (no `nextCursor` ⇒ paging dead after page one) and its unread count.
 *
 * Nesting is read from both places anyway: the two apps' captures disagree about which side
 * `meta` sits on, one of them is a misreading, and accepting either costs a `??`.
 *
 * A row that fails to parse is dropped rather than failing the page: one bad record must not
 * empty the panel.
 */
export const notificationPageEnvelopeSchema = z.unknown().transform((body) => {
  const box = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const data = box.data;

  const rows = rowsFrom(data).length > 0 ? rowsFrom(data) : rowsFrom(box);
  const items = rows.flatMap((row) => {
    const parsed = notificationWireSchema.safeParse(row);
    return parsed.success ? [parsed.data] : [];
  });

  // Beside `data` first (the live shape), then inside it, then at the root for a bare payload.
  const meta: NotificationMeta = {
    ...metaFrom(box),
    ...metaFrom(data && typeof data === 'object' ? (data as Record<string, unknown>).meta : null),
    ...metaFrom(box.meta),
  };

  const nextCursor = meta.nextCursor ?? null;
  const unreadFromRows = items.filter((item) => !item.read).length;

  return {
    items,
    nextCursor,
    // A truthy `hasNextPage` with no cursor would re-request the same page forever.
    hasNextPage: (meta.hasNextPage ?? Boolean(nextCursor)) && Boolean(nextCursor),
    /**
     * The server's count when it sends one. The row-derived fallback only ever sees the rows in
     * hand, so it under-reports past page one — which is exactly why the badge reads this off a
     * `limit=1` call against `meta` rather than counting the feed.
     */
    unreadCount:
      meta.unreadCount ?? (meta.hasUnread ? Math.max(1, unreadFromRows) : unreadFromRows),
    hasUnread: meta.hasUnread ?? (meta.unreadCount ?? unreadFromRows) > 0,
  };
});

export type NotificationPage = z.infer<typeof notificationPageEnvelopeSchema>;

/**
 * `GET /notifications/tracking/unread` → `{ hasUnread }`.
 *
 * The cheap read for the bell's dot. Absent means "nothing unread" rather than an error: a bell
 * is ambient decoration and must never be the thing that surfaces a transport problem.
 */
export const unreadFlagSchema = z.unknown().transform((body) => {
  const box = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const source = (box.data && typeof box.data === 'object' ? box.data : box) as Record<
    string,
    unknown
  >;
  return {
    hasUnread: source.hasUnread === true,
    unreadCount: typeof source.unreadCount === 'number' ? source.unreadCount : null,
  };
});

/**
 * Preference settings.
 *
 * Every category defaults to **on** (US-113356: "All toggles default on for a new user"), so a
 * missing key is read as enabled rather than disabled — the failure mode of a silently
 * suppressed category is much worse than an extra notification.
 */
export const notificationSettingsWireSchema = z
  .object({
    commands: z.boolean().catch(true),
    subscription: z.boolean().catch(true),
    /**
     * The wire spells this **`requestsClaims`** — confirmed against `GET /api/docs-json`,
     * 2026-09-02, whose example is `{ commands, subscription, requestsClaims, deviceLifecycle }`.
     * The app's own category constant is `requestsAndClaims`, so the two are bridged here rather
     * than renaming the constant, which the drawer copy and the toggle mapping both key off.
     * `requestsAndClaims` is still accepted as a fallback so the MSW seam keeps working either way.
     */
    requestsClaims: z.boolean().catch(true),
    requestsAndClaims: z.boolean().catch(true),
    deviceLifecycle: z.boolean().catch(true),
  })
  .partial()
  .transform((settings) => ({
    [NOTIFICATION_CATEGORY.COMMANDS]: settings.commands ?? true,
    [NOTIFICATION_CATEGORY.SUBSCRIPTION]: settings.subscription ?? true,
    [NOTIFICATION_CATEGORY.REQUESTS_AND_CLAIMS]:
      settings.requestsClaims ?? settings.requestsAndClaims ?? true,
    [NOTIFICATION_CATEGORY.DEVICE_LIFECYCLE]: settings.deviceLifecycle ?? true,
  }));

export type NotificationSettings = z.infer<typeof notificationSettingsWireSchema>;
